/**
 * Helper to normalize URLs/slugs to canonical target slugs
 */
const SLUG_ALIASES = {
  "spartling-af-vaeg-foer-maling-tapet-eller-filt": "spartling",
  "maling-af-vaegge-ved-opdatering-af-rum-og-flader": "maling-af-vaegge",
  "maling-af-loft-som-del-af-en-samlet-opdatering-af-rummet": "maling-af-loft",
  "maling-af-doere-ved-renovering-af-boligens-overflader": "maling-af-doere",
  "maling-af-vinduer-ved-renovering-af-husets-ydre": "maling-af-vinduer",
  "maling-af-facade-til-private-boliger-og-parcelhuse": "maling-af-facade",
  "udendoers-malerarbejde-til-facader-traevaerk-og-murvaerk": "udendoers-malerarbejde",
  "udendoers-maleropgaver-der-beskytter-og-forskoenner": "udendoers-malerarbejde",
  "indendoers-malerarbejde-til-opdatering-af-hjemmets-rum": "indendoers-malerarbejde"
};

export function normalizeSlug(slug) {
  if (!slug) return '';
  const clean = slug.replace(/^\//, '').trim();
  return SLUG_ALIASES[clean] || clean;
}

/**
 * Completely removes all <a> tags from an HTML string, preserving the inner text.
 * Used for FAQ sections so that FAQs never contain internal links.
 */
export function stripAllLinks(htmlString) {
  if (!htmlString) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${htmlString}</div>`, 'text/html');
    const container = doc.body.firstChild;
    if (!container) return htmlString;

    const links = container.querySelectorAll('a');
    links.forEach(link => {
      const parent = link.parentNode;
      while (link.firstChild) {
        parent.insertBefore(link.firstChild, link);
      }
      parent.removeChild(link);
    });

    return container.innerHTML;
  } catch (err) {
    console.error('Error stripping links from FAQ:', err);
    return htmlString.replace(/<a\b[^>]*>(.*?)<\/a>/gi, '$1');
  }
}

/**
 * Safely injects internal links into an HTML string.
 * It parses the HTML, traverses text nodes, and replaces keywords with links.
 * Each target slug is linked at most once per page (including existing links).
 * Existing duplicate links to the same target are unwrapped so only 1 link remains.
 * Links in headings or headers are stripped out.
 */
export function injectInternalLinks(htmlString, currentSlug) {
  if (!htmlString) return '';

  // Define the keywords and their target slugs
  const linkDefinitions = [
    {
      keywords: ["spartling af væg", "spartling af vægge", "fuldspartling"],
      slug: "spartling"
    },
    {
      keywords: ["tapetsering"],
      slug: "tapetsering"
    },
    {
      keywords: ["indendørs malerarbejde", "indendørs maleropgaver"],
      slug: "indendoers-malerarbejde"
    },
    {
      keywords: ["maling af vægge", "vægmaling"],
      slug: "maling-af-vaegge"
    },
    {
      keywords: ["maling af loft", "loftmaling"],
      slug: "maling-af-loft"
    },
    {
      keywords: ["maling af døre"],
      slug: "maling-af-doere"
    },
    {
      keywords: ["maling af vinduer"],
      slug: "maling-af-vinduer"
    },
    {
      keywords: ["maling af facade", "facademaling"],
      slug: "maling-af-facade"
    },
    {
      keywords: ["udendørs malerarbejde", "udendørs maleropgaver"],
      slug: "udendoers-malerarbejde"
    },
    {
      keywords: ["opsætning af filt", "opsætning af glasfilt"],
      slug: "opsaetning-af-filt"
    },
    {
      keywords: ["erhvervsmaler", "maling af erhvervslokaler"],
      slug: "erhvervsmaler"
    },
    {
      keywords: ["butiksmaler"],
      slug: "butiksmaler"
    },
    {
      keywords: ["industrimaler"],
      slug: "industrimaler"
    },
    {
      keywords: ["boligforening"],
      slug: "flyttelejligheder"
    },
    {
      keywords: ["forsikringsskader"],
      slug: "forsikringsskader"
    },
    {
      keywords: ["Augustenborg"],
      slug: "maler-i-augustenborg"
    },
    {
      keywords: ["Dybbøl"],
      slug: "maler-i-dybboel"
    },
    {
      keywords: ["Aabenraa"],
      slug: "maler-i-aabenraa"
    },
    {
      keywords: ["Nordborg"],
      slug: "maler-i-nordborg"
    },
    {
      keywords: ["Padborg"],
      slug: "maler-i-padborg"
    },
    {
      keywords: ["Guderup"],
      slug: "maler-i-guderup"
    }
  ];

  try {
    // Parse the HTML string inside a wrapper div
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${htmlString}</div>`, 'text/html');
    const container = doc.body.firstChild;

    if (!container) return htmlString;

    // Keep track of which target slugs have already been linked on this page
    const linkedSlugs = new Set();
    const canonicalCurrent = normalizeSlug(currentSlug);
    if (canonicalCurrent) {
      linkedSlugs.add(canonicalCurrent);
    }

    // 1. Strip/unwrap any <a> tags inside headings or headers
    const headingLinks = container.querySelectorAll('h1 a, h2 a, h3 a, h4 a, h5 a, h6 a, header a, [role="heading"] a');
    headingLinks.forEach(link => {
      const parent = link.parentNode;
      while (link.firstChild) {
        parent.insertBefore(link.firstChild, link);
      }
      parent.removeChild(link);
    });

    // Helper to identify CTA buttons and action links that should NEVER be unwrapped
    function isCtaOrButton(link) {
      if (!link) return false;
      const className = (link.getAttribute('class') || '') + ' ' + (link.className || '');
      if (
        className.includes('inline-flex') ||
        className.includes('btn') ||
        className.includes('rounded-full') ||
        className.includes('shadow') ||
        className.includes('bg-orange') ||
        className.includes('bg-[#')
      ) {
        return true;
      }
      const href = link.getAttribute('href') || '';
      if (
        href === '/kontakt' ||
        href.startsWith('/kontakt') ||
        href.startsWith('#') ||
        href.startsWith('tel:') ||
        href.startsWith('mailto:')
      ) {
        return true;
      }
      return false;
    }

    // 2. Inspect existing <a> links in the HTML
    // - Normalize clean hrefs (e.g. /spartling-af-vaeg-... -> /spartling)
    // - Unwrap duplicate links to the same target so max 1 link remains
    // - Always preserve CTA buttons and action links
    const existingLinks = container.querySelectorAll('a');
    existingLinks.forEach(link => {
      if (isCtaOrButton(link)) {
        return; // Keep all CTA buttons intact
      }

      const href = link.getAttribute('href');
      // Skip internal fragment links (#), phone links (tel:), mailto:
      if (href && !href.startsWith('#') && !href.startsWith('tel:') && !href.startsWith('mailto:')) {
        const canonical = normalizeSlug(href);
        if (canonical) {
          if (linkedSlugs.has(canonical)) {
            // Duplicate link on this page! Unwrap it so only 1 link remains
            const parent = link.parentNode;
            while (link.firstChild) {
              parent.insertBefore(link.firstChild, link);
            }
            parent.removeChild(link);
          } else {
            // First link to this target! Update href to canonical clean URL and record in set
            link.setAttribute('href', `/${canonical}`);
            linkedSlugs.add(canonical);
          }
        }
      }
    });

    // 3. Helper function to recursively traverse and process text nodes for new links
    function traverse(node) {
      // Do not process inside anchor tags or heading/header elements
      const nodeTag = node.nodeName ? node.nodeName.toUpperCase() : '';
      if (nodeTag === 'A' || nodeTag === 'BUTTON' || /^H[1-6]$/.test(nodeTag) || nodeTag === 'HEADER') {
        return;
      }

      // Check parent chain to ensure node is not inside a heading or header element or button
      let parent = node.parentNode;
      while (parent && parent !== container) {
        const parentTag = parent.nodeName ? parent.nodeName.toUpperCase() : '';
        const parentClass = (parent.getAttribute && parent.getAttribute('class')) || '';
        if (
          parentTag === 'A' || 
          parentTag === 'BUTTON' || 
          /^H[1-6]$/.test(parentTag) || 
          parentTag === 'HEADER' ||
          parentClass.includes('btn') ||
          parentClass.includes('inline-flex')
        ) {
          return;
        }
        parent = parent.parentNode;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        let text = node.nodeValue;
        let hasChanges = false;

        let currentParts = [text];

        for (const def of linkDefinitions) {
          if (linkedSlugs.has(def.slug)) {
            continue;
          }

          for (const keyword of def.keywords) {
            if (linkedSlugs.has(def.slug)) {
              break;
            }

            const escapedKeyword = keyword.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
            const regex = new RegExp(`\\b(${escapedKeyword})\\b`, 'i');

            for (let i = 0; i < currentParts.length; i++) {
              if (typeof currentParts[i] !== 'string') continue;

              const match = currentParts[i].match(regex);
              if (match) {
                const matchedText = match[1];
                const matchIndex = match.index;

                const before = currentParts[i].substring(0, matchIndex);
                const after = currentParts[i].substring(matchIndex + matchedText.length);

                const linkEl = doc.createElement('a');
                linkEl.setAttribute('href', `/${def.slug}`);
                linkEl.className = 'text-blue-500 underline hover:text-blue-700 transition-colors';
                linkEl.textContent = matchedText;

                currentParts.splice(i, 1, before, linkEl, after);
                
                linkedSlugs.add(def.slug);
                hasChanges = true;
                break;
              }
            }
          }
        }

        if (hasChanges) {
          const fragment = doc.createDocumentFragment();
          for (const part of currentParts) {
            if (typeof part === 'string') {
              if (part) fragment.appendChild(doc.createTextNode(part));
            } else {
              fragment.appendChild(part);
            }
          }
          node.parentNode.replaceChild(fragment, node);
        }
        return;
      }

      const children = Array.from(node.childNodes);
      for (const child of children) {
        traverse(child);
      }
    }

    traverse(container);
    return container.innerHTML;
  } catch (err) {
    console.error('Error injecting internal links:', err);
    return htmlString;
  }
}
